import { Store } from '@ngrx/store';
import { Injectable, inject } from '@angular/core';
import { combineLatestWith, distinctUntilChanged, map } from 'rxjs';

import { SettingStore } from '../setting/setting.store';
import * as positionSelector from './position.selector';
import { getCurrentValue } from '../../tools/order-calc.tool';
import { SymbolService, TimeService } from '../../modules/shared/services';
import { PositionItem, PositionSummary } from '../../modules/option/models/option.model';
import { Actions, ofType } from '@ngrx/effects';
import { PositionAction } from './position.action';
import { Side } from '@easy-lib/modules/order';

@Injectable({ providedIn: 'root' })
export class PositionStore {
	store: Store = inject(Store);
	actions$ = inject(Actions);
	symbolService = inject(SymbolService);
	timeService = inject(TimeService);
	settingStore = inject(SettingStore);
	Loading = this.store.selectSignal(positionSelector.getPositionLoading);
	PositionIds$ = this.store.select(positionSelector.getPositionIds);
	PositionIsins$ = this.store.select(positionSelector.getPositionIsins);
	MarketDataIsins$ = this.store.select(positionSelector.getMarketDataIsins);

	PositionItems$ = this.store.select(positionSelector.getPositionsItems).pipe(
		combineLatestWith(this.settingStore.setting$, this.timeService.isMarketOpen$.pipe(distinctUntilChanged())),
		map(([items, setting, isMarketOpen]) =>
			items.map(item => {
				const symbol = this.symbolService.symbolByIsin[item.symbolIsin];
				return {
					...item,
					symbolName: item.symbolName,
					strikePrice: symbol?.strikePrice,
					shareDueDate: symbol?.shareDueDate,
					canBuy: symbol?.canBuy,
					canSell: symbol?.canBuy,
					currentValue:
						getCurrentValue(
							item.closingPrice,
							item.lastTradedPrice,
							item.side === Side.Buy ? +symbol?.sellCommission : -symbol.buyCommission,
							setting.portfolioTotalValueCalculateType,
							item.stateColor,
							item.executedQuantity,
							isMarketOpen,
							symbol.csize ?? 1
						) * (item.side === Side.Buy ? 1 : -1)
				} as PositionItem;
			})
		)
	);

	PositionSummary$ = this.PositionItems$.pipe(
		map(items => {
			return {
				totalMargin: items.map(t => t.totalMargin).reduce((a, b) => a + b, 0),
				totalCurrentValue: items.map(t => t.currentValue).reduce((a, b) => a + b, 0)
			} as PositionSummary;
		})
	);

	loadedError$ = this.actions$.pipe(ofType(PositionAction.loadedError));
	positionUpdate$ = this.actions$.pipe(
		ofType(
			PositionAction.loadCashSettlement,
			PositionAction.loadPhysicalSettlement,
			PositionAction.deleteCashSettlement,
			PositionAction.deletePhysicalSettlement
		)
	);

	getPositionsById(id: string) {
		return this.store.select(positionSelector.getPositionsById(id));
	}
	getPositionsItemById(id: string) {
		return this.PositionItems$.pipe(map(items => items.find(t => t.id === id)));
	}
	getPositionsByIsin(symbolIsin: string) {
		return this.store.select(positionSelector.getPositionsByIsin(symbolIsin));
	}
}
